import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/landing/Reveal";
import { SiClaude, SiGooglegemini, SiCursor } from "react-icons/si";
import {
  Bot,
  Palette,
  Code2,
  Wand2,
  Workflow,
  BrainCircuit,
  Rocket,
  ArrowRight,
  Sparkles,
  Cpu,
} from "lucide-react";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const introParas = [
  "AI is transforming every industry, every career, and every business. The people who know how to leverage AI won't just work faster — they'll create more opportunities, earn more, solve bigger problems, and stay ahead in an AI-powered world.",
  "Whether you're an entrepreneur, professional, student, freelancer, executive, creator, or simply someone who wants to stay competitive, b→come AI Academy gives you the skills to confidently use AI in your everyday life and work.",
];

const tools = [
  {
    icon: Bot,
    title: "ChatGPT Mastery",
    tagline: "Move beyond basic prompts.",
    copy: "Advanced prompting, custom GPTs, research workflows, content creation, coding, brainstorming, and automation techniques that save hours every week.",
  },
  {
    icon: SiClaude,
    title: "Claude",
    tagline: "Deep reasoning & long-form work.",
    copy: "Master long-form writing, business strategy, document analysis, research, planning, and complex reasoning with one of today's most capable assistants.",
  },
  {
    icon: SiGooglegemini,
    title: "Gemini",
    tagline: "Google's multimodal AI.",
    copy: "Leverage Google's AI ecosystem for research, collaboration, productivity, coding, and multimodal content creation.",
  },
  {
    icon: Palette,
    title: "Midjourney & AI Design",
    tagline: "Create studio-quality visuals.",
    copy: "Generate professional images, branding, ads, social graphics, presentations, product mockups, and creative assets with AI.",
  },
  {
    icon: SiCursor,
    title: "Cursor & Windsurf",
    tagline: "Build software faster than ever.",
    copy: "Whether you're a seasoned developer or just starting, learn how AI can generate code, debug apps, explain concepts, and accelerate development.",
  },
];

const builds = [
  {
    icon: Code2,
    title: "Coding with AI",
    copy: "Create websites, mobile apps, SaaS products, automations, APIs, and software projects with AI as your development partner — at any experience level.",
  },
  {
    icon: Wand2,
    title: "Prompt Engineering",
    copy: "Unlock the full power of AI with professional prompting frameworks, advanced techniques, and workflows that consistently produce better results.",
  },
  {
    icon: Workflow,
    title: "AI Automation",
    copy: "Eliminate repetitive work. Build intelligent workflows that automate tasks, streamline operations, and give you back hours every week.",
  },
  {
    icon: BrainCircuit,
    title: "AI Agents",
    copy: "Build AI agents that can research, plan, analyze, create content, execute workflows, and complete meaningful work on your behalf.",
  },
  {
    icon: Rocket,
    title: "Build an AI Business",
    copy: "Launch AI-powered businesses, create digital products, automate services, build recurring revenue, and discover new ways to generate income.",
  },
];

const roles = [
  "Entrepreneurs", "Business Owners", "Professionals", "Sales Teams",
  "Marketing Teams", "Software Developers", "Designers", "Consultants",
  "Content Creators", "Freelancers", "Executives", "Students", "Job Seekers",
];

const closing = [
  "AI isn't replacing people. It's replacing people who don't know how to use AI.",
  "The future belongs to those who understand how to work alongside artificial intelligence. Whether your goal is growing your business, advancing your career, increasing your income, launching a startup, or simply becoming more productive, mastering AI gives you a competitive advantage that compounds for years.",
];

/* -------- Animated AI terminal (typewriter) -------- */
const conversations = [
  { q: "Design a 30-day launch plan for my SaaS", a: "Generating milestones, weekly tasks, and a content calendar…" },
  { q: "Build me a React landing page", a: "Scaffolding components, wiring state, styling with Tailwind…" },
  { q: "Automate my weekly report", a: "Workflow created: fetch data → summarize → email every Friday." },
  { q: "Write my cover letter for this role", a: "Drafting a tailored letter using your resume and the job post…" },
];

const AITerminal = () => {
  const [i, setI] = useState(0);
  const [typed, setTyped] = useState("");
  const [showResp, setShowResp] = useState(false);

  useEffect(() => {
    let t;
    const q = conversations[i].q;
    if (typed.length < q.length) {
      t = setTimeout(() => setTyped(q.slice(0, typed.length + 1)), 42);
    } else if (!showResp) {
      t = setTimeout(() => setShowResp(true), 450);
    } else {
      t = setTimeout(() => {
        setShowResp(false);
        setTyped("");
        setI((p) => (p + 1) % conversations.length);
      }, 2600);
    }
    return () => clearTimeout(t);
  }, [typed, showResp, i]);

  return (
    <div
      data-testid="ai-terminal"
      className="mx-auto mt-14 w-full max-w-2xl overflow-hidden rounded-2xl border border-white/12 bg-[#08080A]/90 shadow-[0_20px_80px_-20px_rgba(168,85,247,0.5)] backdrop-blur"
    >
      <div className="flex items-center gap-2 border-b border-white/10 px-5 py-3">
        <span className="h-3 w-3 rounded-full bg-[#F97316]" />
        <span className="h-3 w-3 rounded-full bg-[#F5C35E]" />
        <span className="h-3 w-3 rounded-full bg-[#A855F7]" />
        <span className="ml-3 flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-white/40">
          <Cpu className="h-3.5 w-3.5" /> b→come AI
        </span>
      </div>
      <div className="min-h-[132px] space-y-4 p-6 font-mono text-sm">
        <p className="text-white">
          <span className="text-gradient">›</span> {typed}
          <span className="ml-0.5 inline-block h-4 w-2 -translate-y-0.5 animate-pulse bg-gradient-brand align-middle" />
        </p>
        {showResp && (
          <motion.p
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-start gap-2 text-white/70"
          >
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
            {conversations[i].a}
          </motion.p>
        )}
      </div>
    </div>
  );
};

/* -------- Neural node background -------- */
const NeuralNodes = () => (
  <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
    {[
      { top: "12%", left: "8%", d: 0 },
      { top: "22%", left: "82%", d: 0.6 },
      { top: "62%", left: "16%", d: 1.1 },
      { top: "72%", left: "88%", d: 0.3 },
      { top: "44%", left: "50%", d: 0.9 },
      { top: "85%", left: "42%", d: 1.4 },
    ].map((n, idx) => (
      <span
        key={idx}
        className="absolute h-2 w-2 rounded-full bg-gradient-brand"
        style={{ top: n.top, left: n.left }}
      >
        <span
          className="absolute inset-0 animate-ping rounded-full bg-[#A855F7]"
          style={{ animationDelay: `${n.d}s` }}
        />
      </span>
    ))}
  </div>
);

const ToolCard = ({ item, index, dense }) => {
  const Icon = item.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      data-testid={`ai-card-${index}`}
      className="group relative w-full overflow-hidden rounded-2xl border border-white/10 bg-[#0B0B0E] p-7 transition-colors hover:border-white/25 sm:w-[calc(50%-0.625rem)] lg:w-[calc(33.333%-0.834rem)]"
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full glow-violet opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />
      <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-white/12 bg-white/[0.04] text-white transition-all duration-300 group-hover:border-transparent group-hover:bg-gradient-brand group-hover:text-black">
        <Icon className="h-6 w-6" />
      </div>
      <h4 className="font-display text-lg font-bold uppercase tracking-tight text-white">
        {item.title}
      </h4>
      {item.tagline && (
        <p className="mt-1.5 text-sm font-semibold text-gradient">{item.tagline}</p>
      )}
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{item.copy}</p>
    </motion.div>
  );
};

export const AIAcademy = () => {
  return (
    <section data-testid="ai-academy" id="ai-academy" className="relative overflow-hidden py-28">
      <NeuralNodes />
      <div className="pointer-events-none absolute -left-40 top-20 -z-10 h-[520px] w-[520px] glow-violet blur-3xl opacity-50" />
      <div className="pointer-events-none absolute -right-40 bottom-20 -z-10 h-[480px] w-[480px] glow-orange blur-3xl opacity-40" />

      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <Reveal className="mx-auto max-w-3xl text-center">
          <p className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.3em] text-gradient">
            <Cpu className="h-4 w-4 text-gold" /> b→come AI Academy
          </p>
          <h2 className="mt-6 font-mega text-4xl leading-[0.95] text-white sm:text-6xl lg:text-7xl">
            Master AI.
            <br />
            <span className="text-gradient">Master Your Future.</span>
          </h2>
          <p className="mt-6 font-display text-lg font-bold uppercase tracking-tight text-white/80">
            The Most Valuable Skill You Can Learn.
          </p>
        </Reveal>

        <div className="mx-auto mt-8 max-w-2xl space-y-4 text-center">
          {introParas.map((p, idx) => (
            <Reveal key={idx} delay={0.1 + idx * 0.08}>
              <p className="text-base leading-relaxed text-muted-foreground">{p}</p>
            </Reveal>
          ))}
        </div>

        {/* Animated terminal */}
        <Reveal delay={0.15}>
          <AITerminal />
          <div className="mt-8 flex justify-center">
            <button
              onClick={() => scrollTo("start")}
              data-testid="ai-become-ready-btn"
              className="group inline-flex h-14 items-center justify-center gap-2 rounded-full bg-gradient-brand px-9 text-base font-bold uppercase tracking-wide text-black shadow-[0_16px_60px_-12px_rgba(168,85,247,0.7)] transition-all hover:scale-[1.04]"
            >
              Become AI Ready
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </Reveal>

        {/* Tools */}
        <Reveal className="mt-24 text-center">
          <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">
            Learn the World&rsquo;s Leading AI Tools
          </h3>
        </Reveal>
        <div className="mt-10 flex flex-wrap justify-center gap-5">
          {tools.map((t, i) => (
            <ToolCard key={t.title} item={t} index={i} />
          ))}
        </div>

        {/* Build with AI */}
        <Reveal className="mt-24 text-center">
          <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">
            Build With AI, <span className="text-gradient">Not Just Use It</span>
          </h3>
        </Reveal>
        <div className="mt-10 flex flex-wrap justify-center gap-5">
          {builds.map((b, i) => (
            <ToolCard key={b.title} item={b} index={i} />
          ))}
        </div>

        {/* Learn by building */}
        <Reveal>
          <div className="relative mt-24 overflow-hidden rounded-3xl border border-white/10 bg-[#0A0A0C] p-10 text-center sm:p-14">
            <div className="pointer-events-none absolute left-1/2 top-0 h-40 w-96 max-w-full -translate-x-1/2 glow-violet blur-3xl opacity-50" />
            <h3 className="font-mega text-3xl leading-[0.95] text-white sm:text-4xl">
              Learn by Building
            </h3>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
              Watching videos isn&rsquo;t enough. Every lesson includes hands-on projects,
              interactive exercises, guided challenges, and an AI coach that helps you
              apply what you&rsquo;ve learned immediately.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              {["Build real skills", "Create real projects", "Gain real confidence"].map((x) => (
                <span
                  key={x}
                  className="rounded-full border border-primary/30 bg-primary/10 px-5 py-2.5 text-sm font-semibold text-gold"
                >
                  {x}
                </span>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Roles marquee */}
        <Reveal className="mt-24 text-center">
          <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">
            AI Skills for Every Career
          </h3>
          <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground">
            No matter what you do, AI can help you work smarter and create more value.
          </p>
        </Reveal>
      </div>

      <div className="relative mt-10 overflow-hidden">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-32 bg-gradient-to-r from-[#050505] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-32 bg-gradient-to-l from-[#050505] to-transparent" />
        <div className="flex w-max animate-marquee">
          {[...roles, ...roles].map((r, idx) => (
            <span
              key={idx}
              className="mx-2.5 flex shrink-0 items-center gap-2 rounded-full border border-white/10 bg-[#0C0C0E] px-6 py-3 text-sm font-medium text-white"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-gradient-brand" />
              {r}
            </span>
          ))}
        </div>
      </div>

      {/* Closing */}
      <div className="mx-auto mt-24 max-w-3xl px-6 text-center lg:px-8">
        <Reveal>
          <h3 className="font-mega text-3xl leading-[0.95] text-white sm:text-5xl">
            Stay Ahead of <span className="text-gradient">the Future</span>
          </h3>
          <div className="mt-6 space-y-4">
            {closing.map((c, idx) => (
              <p key={idx} className="text-base leading-relaxed text-muted-foreground">{c}</p>
            ))}
          </div>
          <p className="mt-8 font-display text-xl font-bold uppercase tracking-tight text-white">
            Don&rsquo;t just use AI. <span className="text-gradient">Master it. Build with it. Profit from it.</span>
          </p>
          <p className="mt-3 text-base text-muted-foreground">
            Become the person the future is looking for.
          </p>
          <button
            onClick={() => scrollTo("start")}
            data-testid="ai-start-journey-btn"
            className="group mt-9 inline-flex h-14 items-center justify-center gap-2 rounded-full bg-gradient-brand px-9 text-base font-bold uppercase tracking-wide text-black shadow-[0_16px_60px_-12px_rgba(249,115,22,0.7)] transition-all hover:scale-[1.04]"
          >
            Start Your AI Journey Today
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </button>
        </Reveal>
      </div>
    </section>
  );
};

export default AIAcademy;
